import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useGetPropertyQuery } from '../../features/api/apiSlice';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import SEO from '../../components/seo';

const PropertyDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug_id } = useParams();
  const [user, setUser] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Fetch property data
  const { data: property, isLoading, error } = useGetPropertyQuery(slug_id);

  // Mock user authentication check
  useEffect(() => {
    const mockUser = {
      name: "John Doe",
      email: "john.doe@example.com"
    };
    setUser(mockUser);
  }, []);

  // Handle loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading property details...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error || !property) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">Property Not Found</h3>
          <p className="text-muted-foreground mb-4">
            The property you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate('/property-search-browse')}>
            Back to Search
          </Button>
        </div>
      </div>
    );
  }

  // Format price in UGX
  const formatPrice = (price) => {
    const num = parseInt(price, 10);
    if (isNaN(num)) return 'Price not available';
    return `UGX ${num.toLocaleString()}`;
  };

  const isTaken = property?.is_premium;
  const statusText = isTaken ? 'Taken' : property?.status === '1' ? 'Available' : 'Unavailable';
  const statusBadgeClass = isTaken
    ? 'bg-red-600/10 text-red-600'
    : property?.status === '1'
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-orange-100 text-orange-700';

  const propertySchema = {
    '@context': 'https://schema.org',
    '@type': property?.property_type?.toLowerCase() === 'rent' ? 'Apartment' : 'SingleFamilyResidence',
    name: property?.title,
    description: property?.description || `View this ${property?.category?.category || 'property'} for ${property?.property_type} in ${property?.address}.`,
    url: `https://hirebroker.co/property/${slug_id}`,
    image: property?.title_image,
    address: {
      '@type': 'PostalAddress',
      streetAddress: property?.address || '',
      addressLocality: property?.city || '',
      addressCountry: 'UG'
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'UGX',
      price: parseFloat(property?.price) || 0,
      availability: property?.status === 'sold' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      url: `https://hirebroker.co/property/${slug_id}`
    }
  };

  // Handle image navigation
  const getTotalImages = () => {
    return property.gallery && property.gallery.length > 0 ? property.gallery.length : (property.title_image ? 1 : 0);
  };

  const nextImage = () => {
    const totalImages = getTotalImages();
    if (totalImages > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const prevImage = () => {
    const totalImages = getTotalImages();
    if (totalImages > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  const selectImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Handle contact form
  const handleContactFormChange = (field, value) => {
    setContactForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    alert('Message sent successfully! The property owner will contact you soon.');
    setShowContactForm(false);
    setContactForm({
      name: '',
      email: '',
      phone: '',
      message: ''
    });
  };

  // Handle save property
  const handleSaveProperty = () => {
    setIsSaved(!isSaved);
    if (!isSaved) {
      alert('Property saved to your favorites!');
    } else {
      alert('Property removed from favorites.');
    }
  };

  // Handle logout
  const handleLogout = () => {
    setUser(null);
    navigate('/login');
  };

  // Search context for breadcrumbs
  const searchContext = {
    location: property?.address || "",
    propertyType: property?.category?.category || "",
    priceRange: property?.price ? formatPrice(property.price) : "",
    queryString: ""
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SEO
        title={property?.title}
        description={property?.description || `Find details for ${property?.title} including price, location, and features.`}
        canonicalUrl={`https://hirebroker.co/property/${slug_id}`}
        ogImage={property?.title_image}
        schema={propertySchema}
      />
      <Header user={user} onLogout={handleLogout} />

      {/* Main Content */}
      <main className="pt-16">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 relative z-10">
          <Breadcrumbs
            searchContext={searchContext}
            currentProperty={property}
          />
        </div>

        {/* Property Gallery */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="relative w-full h-[420px] md:h-[540px] bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 rounded-[2rem] overflow-hidden shadow-2xl border border-white/10">
            {property.title_image || (property.gallery && property.gallery.length > 0) ? (
              <>
                <Image
                  src={
                    property.gallery && property.gallery.length > 0
                      ? property.gallery[currentImageIndex]?.image_url
                      : property.title_image
                  }
                  alt={property.title}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-slate-950/30 pointer-events-none" />

                {/* Navigation Arrows */}
                {getTotalImages() > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center backdrop-blur-md border border-white/10 shadow-lg transition-all duration-200"
                      aria-label="Previous image"
                    >
                      <Icon name="ChevronLeft" size={20} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center backdrop-blur-md border border-white/10 shadow-lg transition-all duration-200"
                      aria-label="Next image"
                    >
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </>
                )}

                {/* Image Overlay & Counter */}
                <div className="absolute inset-x-4 bottom-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-white">
                  <div className="space-y-2">
                    <div className="inline-flex items-center gap-2 bg-black/50 backdrop-blur-md px-4 py-2 rounded-full text-sm">
                      <Icon name="Image" size={16} />
                      <span>{property.title}</span>
                    </div>
                    <div className="inline-flex items-center gap-2 bg-black/40 backdrop-blur-md px-4 py-2 rounded-full text-sm">
                      <Icon name="Camera" size={16} />
                      <span>{currentImageIndex + 1} of {getTotalImages()} photos</span>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 bg-black/40 backdrop-blur-md px-4 py-2 rounded-full text-sm">
                    <span className="font-semibold">{statusText}</span>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusBadgeClass}`}>{statusText}</span>
                  </div>
                </div>

                <div className="absolute top-4 left-4">
                  <span className="px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-semibold shadow-lg shadow-primary/20">
                    {property.category?.category || 'Property'}
                  </span>
                </div>

                <div className="absolute top-4 right-4">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="bg-white/10 hover:bg-white/20 text-white rounded-full border border-white/10 shadow-lg"
                    onClick={handleSaveProperty}
                    aria-label="Save property"
                  >
                    <Icon
                      name="Heart"
                      className={`transition-colors ${isSaved ? 'text-red-500 fill-current' : 'text-white'}`}
                    />
                  </Button>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <Icon name="ImageOff" size={48} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No images available</p>
                </div>
              </div>
            )}
          </div>

          {/* Thumbnail Navigation */}
          <div className="mt-4 grid grid-cols-3 sm:grid-cols-5 gap-3">
            {property.gallery && property.gallery.length > 0 ? (
              property.gallery.map((image, index) => (
                <button
                  key={image.id || index}
                  onClick={() => selectImage(index)}
                  className={`group relative overflow-hidden rounded-3xl transition-all duration-300 border-2 ${
                    index === currentImageIndex
                      ? 'border-primary shadow-[0_20px_60px_rgba(59,130,246,0.18)]'
                      : 'border-white/10 hover:border-primary/60'
                  }`}
                >
                  <Image
                    src={image.image_url}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-20 sm:h-24 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {index === currentImageIndex && (
                    <span className="absolute inset-x-0 bottom-0 text-[10px] uppercase tracking-[0.2em] text-white/90 bg-black/40 py-1">
                      Selected
                    </span>
                  )}
                </button>
              ))
            ) : (
              <button
                onClick={() => selectImage(0)}
                className={`overflow-hidden rounded-3xl border-2 transition-all duration-200 ${
                  currentImageIndex === 0
                    ? 'border-primary shadow-md'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <Image
                  src={property.title_image}
                  alt="Main property image"
                  className="w-full h-20 sm:h-24 object-cover"
                />
              </button>
            )}
          </div>
        </div>

        {/* Property Details */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Property Header */}
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold text-foreground mb-2">{property.title}</h1>
                    <div className="flex items-center text-muted-foreground mb-4">
                      <Icon name="MapPin" size={18} className="mr-2" />
                      <span>{property.address}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-2">
                        <Icon name="Eye" size={16} className="text-muted-foreground" />
                        {property.total_view} views
                      </span>
                      <span className="flex items-center gap-2">
                        <Icon name="Clock" size={16} className="text-muted-foreground" />
                        {property.post_created}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadgeClass}`}>
                        {statusText}
                      </span>
                    </div>
                  </div>

                  <div className="text-left lg:text-right">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {formatPrice(property.price)}
                    </div>
                    <div className="text-sm text-muted-foreground capitalize">
                      For {property.property_type}
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => setShowContactForm(true)}
                    className="flex-1 sm:flex-none"
                  >
                    <Icon name="MessageSquare" size={16} className="mr-2" />
                    Contact Owner
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(`tel:${property.mobile}`)}
                    className="flex-1 sm:flex-none"
                  >
                    <Icon name="Phone" size={16} className="mr-2" />
                    Call
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.location.href = `mailto:${property.email}?subject=Inquiry about ${property.title}`}
                    className="flex-1 sm:flex-none"
                  >
                    <Icon name="Mail" size={16} className="mr-2" />
                    Email
                  </Button>
                </div>
              </div>

              {/* Property Description */}
              <div className="bg-card rounded-xl border border-border p-6">
                <h2 className="text-2xl font-semibold text-foreground mb-4">Description</h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                  {property.description}
                </p>
              </div>

              {/* Property Details */}
              <div className="bg-card rounded-xl border border-border p-6">
                <h2 className="text-2xl font-semibold text-foreground mb-6">Property Details</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Basic Information</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Property ID</span>
                        <span className="font-medium">#{property.id}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Category</span>
                        <span className="font-medium">{property.category?.category}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Type</span>
                        <span className="font-medium capitalize">{property.property_type}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Status</span>
                        <span className={`font-medium ${statusBadgeClass}`}>
                          {statusText}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Location Information */}
                  <div>
                    <h3 className="text-lg font-medium text-foreground mb-4">Location</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Address</span>
                        <span className="font-medium">{property.address}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">City</span>
                        <span className="font-medium">{property.city}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">State</span>
                        <span className="font-medium">{property.state}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-border/50">
                        <span className="text-muted-foreground">Country</span>
                        <span className="font-medium">{property.country}</span>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>

              {/* Facilities/Amenities */}
              {property.assign_facilities && property.assign_facilities.length > 0 && (
                <div className="bg-card rounded-xl border border-border p-6">
                  <h2 className="text-2xl font-semibold text-foreground mb-6">Nearby Facilities</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property.assign_facilities.map((facility) => (
                      <div key={facility.id} className="flex items-center p-4 bg-muted/50 rounded-lg">
                        <div className="w-12 h-12 rounded-lg overflow-hidden mr-4 flex-shrink-0">
                          <Image
                            src={facility.image}
                            alt={facility.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-medium text-foreground">{facility.name}</h3>
                          <p className="text-sm text-muted-foreground">{facility.distance} away</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Map Section */}
              {(property.latitude && property.longitude) && (
                <div className="bg-card rounded-xl border border-border p-6">
                  <h2 className="text-2xl font-semibold text-foreground mb-4">Location</h2>
                  <div className="w-full h-64 bg-muted rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.749!2d${property.longitude}!3d${property.latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${property.latitude}%2C${property.longitude}!5e0!3m2!1sen!2sug!4v1698765432100!5m2!1sen!2sug`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map showing location of ${property.title}`}
                    />
                  </div>
                  <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Icon name="MapPin" size={16} className="mr-2" />
                      <span>{property.address}</span>
                    </div>
                    
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Contact Information */}
              <div className="bg-card rounded-xl border border-border p-6 mb-6">
                <h3 className="text-xl font-semibold text-foreground mb-4">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                      <Icon name="User" size={20} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{property.customer_name}</p>
                      <p className="text-sm text-muted-foreground">Property Owner</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => window.open(`tel:${property.mobile}`)}
                    >
                      <Icon name="Phone" size={16} className="mr-2" />
                      {property.mobile}
                    </Button>

                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => window.location.href = `mailto:${property.email}?subject=Inquiry about ${property.title}`}
                    >
                      <Icon name="Mail" size={16} className="mr-2" />
                      {property.email}
                    </Button>

                    <div className="pt-2 border-t border-border">
                      <p className="text-sm text-muted-foreground">
                        <Icon name="MapPin" size={14} className="inline mr-1" />
                        {property.client_address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-card rounded-xl border border-border p-6">
                <h3 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <Button
                    onClick={handleSaveProperty}
                    variant={isSaved ? "default" : "outline"}
                    className="w-full"
                  >
                    <Icon name="Heart" size={16} className="mr-2" />
                    {isSaved ? 'Saved' : 'Save Property'}
                  </Button>

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => navigator.share?.({
                      title: property.title,
                      text: `Check out this property: ${property.title}`,
                      url: window.location.href
                    })}
                  >
                    <Icon name="Share" size={16} className="mr-2" />
                    Share Property
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form Modal */}
        {showContactForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-border">
                <h3 className="text-xl font-semibold">Contact Property Owner</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowContactForm(false)}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              <form onSubmit={handleContactSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) => handleContactFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => handleContactFormChange('email', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) => handleContactFormChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="+256 700 000 000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Message *
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) => handleContactFormChange('message', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                    placeholder={`I'm interested in ${property.title}. Please contact me with more information.`}
                    required
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1"
                  >
                    Send Message
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowContactForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default PropertyDetail;
